"use client";

import Link from "next/link";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type ToastTone = "ok" | "error" | "warn" | "info";

export type ToastShowOptions = {
  duration?: number;
  href?: string;
  actionLabel?: string;
};

type ToastItem = {
  id: string;
  message: string;
  tone: ToastTone;
  /** 0 = sticky until dismissToast(id) */
  duration: number;
  href?: string;
  actionLabel?: string;
};

type ToastContextValue = {
  toast: (message: string, tone?: ToastTone, durationOrOpts?: number | ToastShowOptions) => string;
  toastOk: (message: string, durationOrOpts?: number | ToastShowOptions) => string;
  toastError: (message: string, durationOrOpts?: number | ToastShowOptions) => string;
  dismissToast: (id: string) => void;
};

const DEFAULT_DURATION = 10000;

const ToastContext = createContext<ToastContextValue>({
  toast: () => "",
  toastOk: () => "",
  toastError: () => "",
  dismissToast: () => undefined,
});

export function useToast() {
  return useContext(ToastContext);
}

function normalizeOpts(durationOrOpts?: number | ToastShowOptions): ToastShowOptions {
  if (typeof durationOrOpts === "number") return { duration: durationOrOpts };
  return durationOrOpts ?? {};
}

function ToastIcon({ tone }: { tone: ToastTone }) {
  if (tone === "error") {
    return (
      <span className="toast-icon toast-icon--error" aria-hidden>
        <svg viewBox="0 0 12 12" fill="none">
          <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (tone === "warn") {
    return (
      <span className="toast-icon toast-icon--warn" aria-hidden>
        <svg viewBox="0 0 12 12" fill="none">
          <path d="M6 3.2v3.6M6 8.8h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (tone === "info") {
    return (
      <span className="toast-icon toast-icon--info" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  return null;
}

function ToastBubble({ item, onDone }: { item: ToastItem; onDone: (id: string) => void }) {
  const [exiting, setExiting] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const sticky = item.duration <= 0;

  useEffect(() => {
    if (sticky) return;
    const fadeMs = 260;
    const fadeAt = Math.max(0, item.duration - fadeMs);
    const fadeTimer = window.setTimeout(() => setExiting(true), fadeAt);
    const removeTimer = window.setTimeout(() => doneRef.current(item.id), item.duration);
    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(removeTimer);
    };
  }, [item.duration, item.id, sticky]);

  const className = `toast toast--${item.tone}${exiting ? " toast--out" : ""}${item.href ? " toast--link" : ""}`;
  const body = (
    <>
      <ToastIcon tone={item.tone} />
      <span className="toast-msg">{item.message}</span>
    </>
  );

  if (item.href) {
    return (
      <Link
        href={item.href}
        className={className}
        role="status"
        aria-live="polite"
        onClick={() => onDone(item.id)}
      >
        {body}
      </Link>
    );
  }

  return (
    <div
      className={className}
      role={item.tone === "error" ? "alert" : "status"}
      aria-live={item.tone === "error" ? "assertive" : "polite"}
    >
      {body}
    </div>
  );
}

function ToastViewport({ items, onDone }: { items: ToastItem[]; onDone: (id: string) => void }) {
  if (typeof document === "undefined" || items.length === 0) return null;
  return createPortal(
    <div className="toast-stack" aria-label="Notifications">
      {items.map((item) => (
        <ToastBubble key={item.id} item={item} onDone={onDone} />
      ))}
    </div>,
    document.body,
  );
}

function newToastId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    if (!id) return;
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback((message: string, tone: ToastTone = "ok", durationOrOpts?: number | ToastShowOptions) => {
    const text = message.trim();
    if (!text) return "";
    const opts = normalizeOpts(durationOrOpts);
    const id = newToastId();
    setItems((prev) => [
      ...prev,
      {
        id,
        message: text,
        tone,
        duration: opts.duration ?? DEFAULT_DURATION,
        href: opts.href,
        actionLabel: opts.actionLabel,
      },
    ]);
    return id;
  }, []);

  const toastOk = useCallback(
    (message: string, durationOrOpts?: number | ToastShowOptions) => toast(message, "ok", durationOrOpts),
    [toast],
  );
  const toastError = useCallback(
    (message: string, durationOrOpts?: number | ToastShowOptions) => toast(message, "error", durationOrOpts),
    [toast],
  );

  const value = useMemo(
    () => ({ toast, toastOk, toastError, dismissToast }),
    [toast, toastOk, toastError, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDone={dismissToast} />
    </ToastContext.Provider>
  );
}
